<template>
    <div
        id="map"
        @mouseover="changeCursorStyle"
        @mouseleave="resetCursorStyle"
    >
        <notifications
            role="alert"
            group="layerNotification"
            position="top center"
            :max="1"
            :ignore-duplicates="true"
        />
        <l-map
            ref="leafletMap"
            :aria-label="arialLabelForMap()"
            :crs="crs"
            :zoom="zoom"
            :center="center"
            :options="{zoomControl: true, touchZoom: true, dragging: !isMobile, zoomSnap: 0.5, zoomDelta: 1, maxZoom: 15, minZoom:minZoomLevel}"
            @update:center="centerUpdated"
            @update:zoom="zoomUpdated"
            @update:bounds="boundsUpdated"
            @locationerror="showLocationError"
            @locationfound="saveCoordinates"
            @click="locateMapClick"
        >
            <l-tile-layer
                ref="backgroundMap"
                name="backgroundMap"
                :url="fixedBackgroundMapTilesUrl"
                :attribution="attribution"
            />
            <!--<l-control-zoom position="topright" :zoomInTitle="zoomInTitle" :zoomOutTitle="zoomOutTitle"></l-control-zoom>-->
        </l-map>
    </div>
</template>

<script lang="ts">
    import { Vue, Component, Watch, Prop } from "vue-facing-decorator"
    import { toRaw } from "vue"
    import L from "leaflet"
    import { LMap, LTileLayer, LMarker } from "vue3-leaflet"
    import { MarkerClusterGroup } from "leaflet.markercluster"
    import "proj4leaflet"
    import ApiService from "../services/ApiService"
    import LaajakaistaJaPuhelinService from "../services/LaajakaistaJaPuhelinService"
    import PostiService from "../services/PostiService"
    import TieliikenneService from "../services/TieliikenneService"
    import VesiliikenneService from "../services/VesiliikenneService"
    import TVJaRAdioService from "../services/TVJaRadioService"
    import SearchResultModel from "@/models/searchresultmodel"
    import DynamicRightSlotData from "../models/dynamicrighslotdata"
    import LatLngPoint from "../models/latlngpoint"
    import proj4 from "proj4"
    import ApiDataModel from "@/models/apidatamodel"
    import MarkerUtils from "../helpers/markerUtils"
    import { getPostiLangTerm } from "../helpers/postiMarkerUtils"
    import { getLiikenneLangTerm } from "../helpers/liikenneMarkerUtils"
    import { AreaTypes, GeometryTypes } from "@/helpers/enums"
    import LocateMeHelper from "@/helpers/locateMeHelper"
    import booleanPointInPolygon from "@turf/boolean-point-in-polygon"
    import * as turf from "@turf/helpers"
    import InfokartatService from "@/services/InfokartatService"
    import AreaUtils from "@/helpers/areaUtils"
    import ViatJaHairiotService from "@/services/ViatJaHairiotService"
    import RadiomikrofonitaajuudetService from "@/services/RadiomikrofonitaajuudetService"
    import BittimittariService from "@/services/BittimittariService"
    import { store } from '@/store/store'
    import sanitizeHtml from "sanitize-html"

    @Component({
        components: {
            LMap,
            LTileLayer,
            LMarker
            //    LControl,
            //    LControlZoom
        }
    })

    // nimetty luokka uudelleen Map -> MapClass, koska Map on jo käytössä js/typescriptissä (https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Map)
    export default class MapClass extends Vue {
        @Prop({ type: Boolean, default: false }) disableLayerEvents!: boolean // Disabloidaan kartan tasojen eventit
        AreaUtils = new AreaUtils()
        pageNameRadiomikrofonitaajuudet: string = "Radiomikrofonitaajuudet"

        locateMapClick(event: L.LeafletMouseEvent): void {
            if (store.getters.currentPage === this.pageNameRadiomikrofonitaajuudet) {
                this.saveCoordinates(event, true)
                this.clearLocationMarker()
                store.dispatch("setLoading", { name: this.pageNameRadiomikrofonitaajuudet, value: true })

                const apiservice: ApiService = new ApiService(store.getters.baseHref)
                // Tarkistetaan onko paikka Suomen rajojen sisällä
                apiservice.getMunicipalityByWGS84(event.latlng.lat, event.latlng.lng).then(async (result) => {
                    const obj = JSON.parse(result)
                    if (obj.Name !== null && obj.Name !== "") {
                        // Koordinaateilla löytyi kunta. Paikka on Suomen rajojen sisällä.
                        store.dispatch("setFrequencySearchData", { lat: event.latlng.lat, lng: event.latlng.lng, f1: store.getters.frequencySearchData.f1, f2: store.getters.frequencySearchData.f2 })
                        this.addLocationMarker([event.latlng.lat, event.latlng.lng])
                        this.locateMeHelper.setLocationPointView(event.latlng.lat, event.latlng.lng)
                    }
                    else {
                        // Poistetaan righslotista edellisen paikan taajuudet ja näytetään infoteksti
                        const rightslotData = new DynamicRightSlotData("1")
                        rightslotData.textContent = this.$t("App.Radiomikrofonitaajuudet.RightSlot.OutsideOfBordersText") ?? ""
                        store.dispatch("setRightSlotData", rightslotData)
                    }
                }).finally(() => {
                    store.dispatch("setLoading", { name: this.pageNameRadiomikrofonitaajuudet, value: false })
                })
                this.addLocationMarker([store.getters.frequencySearchData.lat, store.getters.frequencySearchData.lng])
            }
        }

        changeCursorStyle() {
            if (store.getters.currentPage === this.pageNameRadiomikrofonitaajuudet) {
                const mapAndMarkerElements = toRaw(Array.from(document.querySelectorAll("#map > div.leaflet-map-container.leaflet-container.leaflet-touch.leaflet-fade-anim.leaflet-grab.leaflet-touch-drag.leaflet-touch-zoom, #map > div.leaflet-map-container.leaflet-container.leaflet-touch.leaflet-fade-anim.leaflet-grab.leaflet-touch-drag.leaflet-touch-zoom > div.leaflet-pane.leaflet-map-pane > div.leaflet-pane.leaflet-marker-pane > img")))
                mapAndMarkerElements.forEach(element => {
                    (element as HTMLElement).style.cursor = "crosshair"
                })
            }
        }

        resetCursorStyle() {
            if (store.getters.currentPage === this.pageNameRadiomikrofonitaajuudet) {
                const mapAndMarkerElements = toRaw(document.querySelectorAll("#map > div.leaflet-map-container.leaflet-container.leaflet-touch.leaflet-fade-anim.leaflet-grab.leaflet-touch-drag.leaflet-touch-zoom, #map > div.leaflet-map-container.leaflet-container.leaflet-touch.leaflet-fade-anim.leaflet-grab.leaflet-touch-drag.leaflet-touch-zoom > div.leaflet-pane.leaflet-map-pane > div.leaflet-pane.leaflet-marker-pane > img"))
                mapAndMarkerElements.forEach(element => {
                    (element as HTMLElement).style.cursor = "grab"
                })
            }
        }

        created(): void {
            store.dispatch("setLoading", { name: "mapLoading", value: true })
            this.attribution = this.$t("App.Map.MapProviderText")
        }

        async mounted(): Promise<void> {
            this.$nextTick(async () => {
                // Tallennetaan mapObject geomanMap muuttujaan
                if (!(this.$refs.leafletMap as typeof LMap)) {
                    store.dispatch("setLoading", { name: "mapLoading", value: false })
                    return
                }

                this.geomanMap = (this.$refs.leafletMap as typeof LMap).getMap()
                if (this.geomanMap === null) {
                    store.dispatch("setLoading", { name: "mapLoading", value: false })
                    return
                }
                // this.geomanMap.attributionControl.setPrefix(false)
                this.geomanMap.options.attributionControl = true
                L.control.attribution().setPrefix(false)
                L.tileLayer(this.fixedBackgroundMapTilesUrl, { attribution: this.attribution }).addTo(this.geomanMap)
                const tileElement = document.querySelector("#map > div.leaflet-map-container > div.leaflet-control-container > div.leaflet-bottom.leaflet-right")
                const tileDiv = document.createElement('div')
                tileDiv.className = 'leaflet-control-attribution leaflet-control'
                tileDiv.append(this.attribution ?? "")
                tileElement?.appendChild(tileDiv)

                if (this.geomanMap.options?.zoom !== undefined) {
                    this.zoom = this.geomanMap.options.zoom
                }

                this.lMap.on("moveend", () => {
                    if (this.geomanMap.getCenter() !== undefined) {
                        this.centerUpdated(this.geomanMap.getCenter())
                    }
                })

                this.lMap.on("zoomend", () => {
                    if (this.geomanMap.getZoom() !== undefined) {
                        this.zoomUpdated(this.geomanMap.getZoom())
                    }
                    if (this.geomanMap.getBounds() !== undefined) {
                        this.boundsUpdated(this.geomanMap.getBounds())
                    }
                })

                this.lMap.on("click", (e) => {
                    this.locateMapClick(e)
                })

                this.lMap.on("locationerror", (e) => {
                    this.showLocationError(e)
                })

                this.lMap.on("locationfound", (e) => {
                    this.saveCoordinates(e)
                })

                // Karttaobjekti
                await this.setMapBounds()

                // Poistetaan tabindex kartalta, sitä ei haluta saavutettavuuden takia. (screen readerit)
                const map = toRaw(document.getElementsByClassName("leaflet-map-container")[0])
                if (map != null) {
                    map.removeAttribute("tabindex")
                }

                // KiinteanVerkonSaatavuusRuudut iFramen tapauksessa listener pitää rekisteröidä mounted()-metodissa
                if (this.dataUrlToLoad !== null && this.dataUrlToLoad.url !== undefined && this.dataUrlToLoad.url.includes("KiinteanVerkonSaatavuusRuudut")) {
                    this.initRuudutMapListener()
                }

                // Käyttäjän sijainti paikannettu
                this.lMap.on("locationfound", (e: L.LocationEvent) => {
                    const includeNearestPoints = this.useNearestPointsSearch.includes(this.currentLayer)
                    if (includeNearestPoints && store.getters.currentPage !== this.pageNameRadiomikrofonitaajuudet) {
                        this.zoomToCoordinates(e.latlng.lat, e.latlng.lng, includeNearestPoints)
                    }
                    // Radiomikrofonitaajuudet-sivulla on oma käsittelynsä
                    else if (store.getters.currentPage === this.pageNameRadiomikrofonitaajuudet) {
                        // tallennetaan Paikanna minut -painikkeen koordinaatit radiomikrofonitaajuuksien hakua varten storeen
                        store.dispatch("setFrequencySearchData", { lat: e.latlng.lat, lng: e.latlng.lng, f1: store.getters.frequencySearchData.f1, f2: store.getters.frequencySearchData.f2 })
                    }
                    else {
                        this.lMap.setView(e.latlng, 8)
                    }
                    // asetetaan marker sijaintiin ja haetaan pisteen osoite hakukenttään
                    this.locateMeHelper.setLocationPointView(e.latlng.lat, e.latlng.lng)
                })

                store.dispatch("setLoading", { name: "mapLoading", value: false })
            })
        }

        useNearestPointsSearch: string[] = ["LupaJaRekisterointitoimipaikat", "Rekisterointitoimipaikat"]
        // attribution: string | undefined = this.$t("App.Map.MapProviderText")
        attribution: string | undefined
        // mobiilinäytöllä aloitetaan tasosta 1.5 ja min-zoom on myös 1.5 ja työpöydällä 2, myös zoomOriginissa sama
        zoom: number = L.Browser.mobile ? 1.5 : 2
        center = [64.35, 26.90]
        zoomOrigin: number = L.Browser.mobile ? 1.5 : 2
        centerOrigin = [64.35, 26.90]
        bounds = null
        geomanMap: any = null
        apiService: ApiService = new ApiService(store.getters.baseHref)
        dataLayer: L.FeatureGroup = L.featureGroup()
        currentLayer: string = ""
        currentApiData: Array<DynamicRightSlotData> = []
        MarkerUtils = new MarkerUtils(store.getters.contentServiceUrl, store.getters.contentServiceTimeStamp)
        locateMeHelper: LocateMeHelper = new LocateMeHelper()
        laajakaistaJaPuhelinService: LaajakaistaJaPuhelinService = new LaajakaistaJaPuhelinService()
        postiService: PostiService = new PostiService()
        tieliikenneService: TieliikenneService = new TieliikenneService()
        vesiliikenneService: VesiliikenneService = new VesiliikenneService()
        tvJaRadioService: TVJaRAdioService = new TVJaRAdioService()
        infokartatService: InfokartatService = new InfokartatService()
        viatJaHairiotService: ViatJaHairiotService = new ViatJaHairiotService()
        radiomikrofonitaajuudetService: RadiomikrofonitaajuudetService = new RadiomikrofonitaajuudetService()
        bittimittariService: BittimittariService = new BittimittariService()
        visiblemarkers: Map<string, number> | null = null
        markersData: Map<string, string> | null = null
        markerClusterGroup = new MarkerClusterGroup({
            showCoverageOnHover: false
        })

        makeTheCall = true
        coordinatesOfPreviousSquareCall: Array<string> = [] // koordinaatit joilla tehty edellinen ruutuhaku
        apiData: ApiDataModel[] | null = null
        selectedLayer: L.Layer | null = null
        locationMarkerLayer: L.Layer = new L.Layer()
        selectedMarkerLayer: L.Layer = new L.Layer()
        isMobile = L.Browser.mobile

        // Proj4Leaflet coordinates projection from WGS84 to ETRS-TM35FIN (EPSG:3067)
        crs = new L.Proj.CRS("EPSG:3067",
                             "+proj=utm +zone=35 +ellps=GRS80 +units=m +no_defs", {
                                 origin: [-548576, 8388608],
                                 bounds: L.bounds([-548576, 8388608], [1548576, 6291456]),
                                 resolutions: [
                                     8192, 4096, 2048, 1024, 512, 256,
                                     128, 64, 32, 16, 8, 4, 2, 1, 0.5,
                                     0.25, 0.125, 0.0625, 0.03125, 0.015625
                                 ]
                             })

        get fixedBackgroundMapTilesUrl(): string {
            return store.getters.mapServicesUrl + "taustakartta/default/" + this.coordinateSystem() + "/{z}/{y}/{x}.png"
        }

        get lMap(): L.Map {
            return this.geomanMap
        }

        get minZoomLevel(): number {
            // mobiilinäytöllä aloitetaan tasosta 1.5 ja min-zoom on myös 1.5 ja työpöydällä 2
            if (this.isMobile) return 1.5
            return 2
        }

        get zoomInTitle(): string | undefined {
            return this.$t("App.map.ZoomInTitle")
        }

        get zoomOutTitle(): string | undefined {
            return this.$t("App.map.ZoomOutTitle")
        }

        get currentZoomLevel(): number {
            return this.lMap.getZoom()
        }

        // Paikanna minut
        get locateMeClick(): boolean {
            return store.getters.locateMeClick
        }

        get activeLang(): SearchResultModel {
            return store.getters.activeLang
        }

        @Watch("activeLang")
        async onActiveLang(): Promise<void> {
            // Kielivalinta muuttunut. Päivitetään attribution text (kartan alanurkassa).
            const attributionControl = L.control.attribution()
            const newAttribution = this.$t("App.Map.MapProviderText")
            if (this.lMap && newAttribution && this.attribution) {
                attributionControl.removeAttribution(this.attribution)
                this.attribution = newAttribution
                attributionControl.addAttribution(this.attribution)
            }
        }

        @Watch("locateMeClick")
        onLocateMeClick(): void {
            this.clearSelections()
            this.clearLocationMarker()
            this.lMap.locate({ setView: false })
        }

        // HUOM: jos showmarkerin laittaa oletuksena true, tällöin paikanna minut toiminto lisää markkerin näytölle. Tehty tulevaisuudem vaatimuksia ajatellen valmiiksi.
        // Lisäksi mapObjectClicked metodissa kutsutaan myös saveCoordinates ja jos halutaan että käyttäjän klikattua kartalta esim kuntaa/maakuntaa lisätään markkeri niin true ko. kutsuun.
        // tämän markkerin siivouksia ei ole vielä toteutettu eli jos esim. vaihdetaan aihetta jne.
        saveCoordinates(e, showMarker: boolean = false): void {
            const coords: LatLngPoint = new LatLngPoint(e.latlng.lat, e.latlng.lng)
            store.dispatch("setCoordinates", coords)
            if (showMarker) this.addLocationMarker([coords.lat, coords.lng])
        }

        // Tyhjäntä paikannusta
        get stopLocate(): boolean {
            return store.getters.stopLocate
        }

        @Watch("stopLocate")
        onStopLocate(): void {
            if (this.lMap !== null) {
                this.lMap.stopLocate()
                this.lMap.stop()
            }
            this.center = this.centerOrigin
            this.zoom = this.zoomOrigin
        }

        showLocationError(error): void {
            let msg = this.$t("ErrorMessages.LocationBlocked")
            if (error.code > 1) {
                msg = this.$t("ErrorMessages.GeolocationUndefined")
            }
            this.$notify({
                group: "infoNotification",
                title: sanitizeHtml(this.$t("ErrorMessages.Header")),
                text: sanitizeHtml(msg),
                duration: 10000,
                type: "error"
            })
        }

        // Osoite haku
        get searchAddressSelected(): SearchResultModel | null {
            return store.getters.searchAddressSelected
        }

        @Watch("searchAddressSelected")
        onSearchAddressSelected(): void {
            if (this.searchAddressSelected !== null) {
                const includeNearestPoints = this.useNearestPointsSearch.includes(this.currentLayer)
                // Radiomikrofonit-sivulla on erilainen käsittely kuin muilla sivuilla
                if (store.getters.currentPage === this.pageNameRadiomikrofonitaajuudet) {
                    // Siirretään karttanäkymää vain, jos haettu piste on nykyisen karttanäkymän ulkopuolella
                    if (this.isPointInsideOfMapBounds(this.searchAddressSelected.coordinates.lat, this.searchAddressSelected.coordinates.lng) === false) {
                        this.zoomToCoordinates(this.searchAddressSelected.coordinates.lat, this.searchAddressSelected.coordinates.lng, false, this.currentZoomLevel)
                    }
                }
                else {
                    this.zoomToCoordinates(this.searchAddressSelected.coordinates.lat, this.searchAddressSelected.coordinates.lng, includeNearestPoints)
                }
                this.clearSelections()
                // otetaan koordinaatit talteen ja tehdään vapaiden taajuuksien haku, mikäli ollaan radiomikrofonitaajuudet sivulla
                if (store.getters.currentPage === this.pageNameRadiomikrofonitaajuudet) {
                    store.dispatch("setFrequencySearchData", { lat: this.searchAddressSelected.coordinates.lat, lng: this.searchAddressSelected.coordinates.lng, f1: store.getters.frequencySearchData.f1, f2: store.getters.frequencySearchData.f2 })
                    // Radiomikrofonien haku tehdään Map.vue:ssa aina tämän watcherin kautta, jotta haku laukeaa aina vain yhden kerran per haku
                    this.radiomikrofonitaajuudetService.availableFrequenciesSearch()
                }
                this.addLocationMarker([this.searchAddressSelected.coordinates.lat, this.searchAddressSelected.coordinates.lng])
            }
            else {
                // osoitehakukenttä on tyhjennetty käyttäjän toimesta
                this.clearLocationMarker()
            }
        }

        // dynaamisen apikutsun osoite
        get dataUrlToLoad(): { url: string, type: string, params?: URLSearchParams, clearLocationMarker: boolean } {
            return store.getters.dataUrlToLoad ?? ""
        }

        // Kun datan haun osoite muuttuu, tehdään uusi rajapintakutsu
        @Watch("dataUrlToLoad")
        async onDataUrlToLoad(): Promise<void> {
            if (this.dataUrlToLoad) {
                store.dispatch("setLoading", { name: "loadMapData", value: true })
                try {
                    if (this.dataUrlToLoad.url.includes("KiinteanVerkonSaatavuusRuudut")
                        || this.dataUrlToLoad.url.includes("RuutuKlusteritVuorokaudenAika")) {
                        if (this.lMap == null) {
                            store.dispatch("setLoading", { name: "loadMapData", value: false })
                            return
                        }
                        const filters = new URLSearchParams()
                        const bounds = this.lMap.getBounds()
                        const zoomLevel = this.lMap.getZoom()
                        const northBound = bounds.getNorth().toString()
                        const eastBound = bounds.getEast().toString()
                        const southBound = bounds.getSouth().toString()
                        const westBound = bounds.getWest().toString()
                        filters.append("north", northBound)
                        filters.append("east", eastBound)
                        filters.append("south", southBound)
                        filters.append("west", westBound)
                        filters.append("zoom", this.getZoom(zoomLevel))

                        // RuutuKlusteritVuorokaudenAika -sivulla lisätään parametri vuorokaudenAikaId
                        if (this.dataUrlToLoad.url.includes("RuutuKlusteritVuorokaudenAika")
                            && this.dataUrlToLoad.params?.get("vuorokaudenAikaId") !== null) {
                            filters.append("vuorokaudenAikaId", this.dataUrlToLoad.params?.get("vuorokaudenAikaId") ?? "")
                        }

                        this.initRuudutMapListener()

                        const data = await this.apiService.dynamicApiCall(this.dataUrlToLoad.url, filters)
                        await this.handleData(data, this.dataUrlToLoad.type)
                    }
                    else {
                        const data = await this.apiService.dynamicApiCall(this.dataUrlToLoad.url, this.dataUrlToLoad.params)
                        await this.handleData(data, this.dataUrlToLoad.type)
                    }
                }
                catch (err) {
                    console.log(err)
                    // TODO: virheenkäsittely? tähän päädytään dynamicApicalin virheen kautta esim 404, 500 tms.
                    // DynamicApicalissa on virheen käsittely mutta siellä tapahtunut virhe keskeyttää tämän puolen käsittelyn
                }

                store.dispatch("setLoading", { name: "loadMapData", value: false })
            }
            else {
                // Tyhjennetään kartta
                this.clearMap()
                this.center = this.centerOrigin
                this.zoom = this.zoomOrigin
                this.currentLayer = ""
                this.checkZoomLevel()
                this.clearLocationMarker()
                await this.clearSelectedMarker()
            }
        }

        // Lataa ruutuklusterit kartalle karttaa liikuttaessa tai zoomatessa
        initRuudutMapListener() {
            // kartan liikuttamisen/zoomin käsittely
            this.geomanMap.on("moveend", async () => {
                if (this.dataUrlToLoad !== null && this.dataUrlToLoad.url !== undefined && (this.dataUrlToLoad.url.includes("KiinteanVerkonSaatavuusRuudut")
                    || this.dataUrlToLoad.url.includes("RuutuKlusteritVuorokaudenAika"))) {
                    const moveendZoomLevel = this.lMap.getZoom()

                    if (moveendZoomLevel >= this.getMinZoomLevel()) {
                        const filters = new URLSearchParams()
                        this.makeTheCall = true
                        const moveendNorthBound = this.lMap.getBounds().getNorth().toString()
                        const moveendEastBound = this.lMap.getBounds().getEast().toString()
                        const moveendSouthBound = this.lMap.getBounds().getSouth().toString()
                        const moveendWestBound = this.lMap.getBounds().getWest().toString()
                        const moveendZoom = this.getZoom(moveendZoomLevel)

                        // tarkistetaan koordinaatit ja estetään näin useamman haun tekeminen kartan siirron/zoomin yhteydessä
                        if (this.coordinatesOfPreviousSquareCall.includes(moveendNorthBound)
                            && this.coordinatesOfPreviousSquareCall.includes(moveendEastBound)
                            && this.coordinatesOfPreviousSquareCall.includes(moveendSouthBound)
                            && this.coordinatesOfPreviousSquareCall.includes(moveendWestBound)
                            && this.coordinatesOfPreviousSquareCall.includes(moveendZoom)) {
                            this.makeTheCall = false
                        }

                        if (this.makeTheCall) {
                            filters.append("north", moveendNorthBound)
                            filters.append("east", moveendEastBound)
                            filters.append("south", moveendSouthBound)
                            filters.append("west", moveendWestBound)
                            filters.append("zoom", moveendZoom)

                            this.coordinatesOfPreviousSquareCall = []
                            this.coordinatesOfPreviousSquareCall.push(moveendNorthBound)
                            this.coordinatesOfPreviousSquareCall.push(moveendEastBound)
                            this.coordinatesOfPreviousSquareCall.push(moveendSouthBound)
                            this.coordinatesOfPreviousSquareCall.push(moveendWestBound)
                            this.coordinatesOfPreviousSquareCall.push(moveendZoom)

                            if (this.dataUrlToLoad.url.includes("RuutuKlusteritVuorokaudenAika")
                                && this.dataUrlToLoad.params?.get("vuorokaudenAikaId") !== null) {
                                filters.append("vuorokaudenAikaId", this.dataUrlToLoad.params?.get("vuorokaudenAikaId") ?? "")
                            }

                            const data = await this.apiService.dynamicApiCall(this.dataUrlToLoad.url, filters)
                            await this.handleData(data, this.dataUrlToLoad.type)
                        }
                    }
                }
            })
        }

        getZoom(zoomLevel: number) {
            if (!this.isMobile) {
                // desktop zoom
                // pyöristetään zoomLevel ylöspäin isoilla näytöillä, koska nopea zoomailu saattaa muuttaa zoomLevelin desimaaliksi
                zoomLevel = Math.ceil(zoomLevel)
            }

            if (this.dataUrlToLoad.url.includes("RuutuKlusteritVuorokaudenAika")) {
                if (zoomLevel <= 6) {
                    return "10000"
                }
                else {
                    return "1000"
                }
            }
            else {
                if (zoomLevel <= 10) {
                    return "1000"
                }
                else {
                    return "250"
                }
            }
        }

        async handleData(data, type: string) {
            // Tunnistetaan typen avulla minkä tyyppistä dataa ollaan tuomassa. Datamalleja voi olla useita erilaisia.
            // Pilkotaan koordinaatit ja muu data erikseen ApiDataModel muotoon
            let apiData: ApiDataModel[] = []
            let legend = ""

            // 1.10.2024 Kartan zoomaustason voi resetoida tällä, jos joskus tarvitaan:
            // this.lMap.setView([64.35, 26.90], this.zoomOrigin)

            // poistetaan annetun sijainnin markkeri, mikäli alisivu tai päätaso vaihdettu. Ei poisteta aihetta vaihdettaessa
            if (store.getters.dataUrlToLoad.clearLocationMarker) {
                this.clearLocationMarker()
                this.setLocationElementVisibility()
            } // sen sijaan mikäli kyseessä aiheen vaihto ja markeri löytyy, tarkistetaan onko sijainnin markkeri näkyvillä
            else if (this.locationMarkerLayer instanceof L.Marker) {
                this.checkLocationMarkerVisibility()
                this.setLocationElementVisibility()
            }

            await this.clearSelectedMarker()

            switch (type) {
                case "PostiHaeToimipaikkoja":
                    apiData = await this.postiService.handleDataPosti(data)
                    legend = await this.postiService.getToimipaikkaTyyppiLegend()
                    break
                case "PostiTarkasteleMaaraa":
                    apiData = await this.postiService.handleDataPostiMaarat(data)
                    break
                case "LupaJaRekisterointitoimipaikat": // tieliikenne
                    apiData = await this.tieliikenneService.handleDataLupaJaRekisterointitoimipaikat(data)
                    legend = this.tieliikenneService.getToimipaikkaTyyppiLegend()
                    break
                case "Liikenneonnettomuudet":
                    apiData = await this.tieliikenneService.handleDataLiikenneonnettomuudet(data)
                    switch (this.AreaUtils.getAreaTypeFromData(data)) {
                        case AreaTypes.Municipality:
                            legend = this.tieliikenneService.getLiikenneonnettomuudetLegendMunicipalities()
                            break
                        case AreaTypes.Province:
                            legend = this.tieliikenneService.getLiikenneonnettomuudetLegendProvinces()
                            break
                    }
                    break
                case "LiikennekaytossaHenkiloAutot":
                    apiData = await this.tieliikenneService.handleDataLiikenteenTilastotKayttovoimittain(type, data)
                    // Legendia ei näytetä koko maa näkymässä
                    if (apiData.length > 0 && (apiData[0].featureData.kuntanumero !== null || apiData[0].featureData.maakuntanumero !== null)) {
                        legend = this.tieliikenneService.getLiikennekaytossaHenkiloAutotLegend(apiData[0].featureData.kayttovoima)
                    }
                    break
                case "LiikennekaytossaHenkiloAutotKeskiIka":
                    apiData = await this.tieliikenneService.handleDataLiikennekaytossaHenkiloAutotKeskiIka(data)
                    // Legendia ei näytetä koko maa näkymässä
                    if (apiData.length > 0 && (apiData[0].featureData.kuntanumero !== null || apiData[0].featureData.maakuntanumero !== null)) {
                        legend = this.tieliikenneService.getLiikennekaytossaHenkiloAutotKeskiIkaLegend()
                    }
                    break
                case "EnsirekisteroidytHenkiloAutot":
                    apiData = await this.tieliikenneService.handleDataLiikenteenTilastotKayttovoimittain(type, data)
                    // Legendia ei näytetä koko maa näkymässä
                    if (apiData.length > 0 && (apiData[0].featureData.kuntanumero !== null || apiData[0].featureData.maakuntanumero !== null)) {
                        legend = this.tieliikenneService.getEnsirekisteroidytHenkiloAutotLegend(apiData[0].featureData.kayttovoima)
                    }
                    break
                case "EnsirekisteroidytHenkiloAutotPaastot":
                    apiData = await this.tieliikenneService.handleDataEnsirekisteroidytHenkiloAutotPaastot(data)
                    // Legendia ei näytetä koko maa näkymässä
                    if (apiData.length > 0 && (apiData[0].featureData.kuntanumero !== null || apiData[0].featureData.maakuntanumero !== null)) {
                        legend = this.tieliikenneService.getEnsirekisteroidytHenkiloAutotPaastotLegend()
                    }
                    break
                case "MyonnetytTaksiluvat":
                    apiData = await this.tieliikenneService.handleDataMyonnetytTaksiluvat(data)
                    // Legendia ei näytetä koko maa näkymässä
                    if (apiData.length > 0 && (apiData[0].featureData.kuntanumero !== null || apiData[0].featureData.maakuntanumero !== null)) {
                        legend = this.tieliikenneService.getMyonnetytTaksiluvatLegend()
                    }
                    break
                case "VoimassaOlevatAjokortit":
                    apiData = await this.tieliikenneService.handleDataVoimassaOlevatAjokortit(data)
                    // Legendia ei näytetä koko maa näkymässä
                    if (apiData.length > 0 && (apiData[0].featureData.kuntanumero !== null || apiData[0].featureData.maakuntanumero !== null)) {
                        legend = this.tieliikenneService.getVoimassaOlevatAjokortitLegend()
                    }
                    break
                case "Rekisterointitoimipaikat": // vesiliikenne
                    apiData = await this.vesiliikenneService.handleDataRekisterointitoimipaikat(data)
                    legend = this.vesiliikenneService.getToimipaikkaTyyppiLegend()
                    break
                case "RekisteroidytVesikulkuneuvot":
                    apiData = await this.vesiliikenneService.handleDataRekisteroidytVesikulkuneuvot(data)
                    // Legendia ei näytetä koko maa näkymässä
                    if (apiData.length > 0 && (apiData[0].featureData.kuntanumero !== null || apiData[0].featureData.maakuntanumero !== null)) {
                        legend = this.vesiliikenneService.getRekisteroidytVesikulkuneuvotLegend()
                    }
                    break
                case "Laajakaistahankkeet":
                    apiData = await this.laajakaistaJaPuhelinService.handleDataLaajakaistahankkeet(data)
                    legend = this.laajakaistaJaPuhelinService.getLaajakaistahankkeetLegend()
                    break
                case "KiinteanLaajakaistanSaatavuus":
                    apiData = await this.laajakaistaJaPuhelinService.handleDataKiinteanLaajakaistanSaatavuus(data)
                    legend = this.laajakaistaJaPuhelinService.getKVSaatavuusLegend(data)
                    break
                case "KiinteanLaajakaistanSaatavuusModal":
                    await this.laajakaistaJaPuhelinService.handleDataKiinteanLaajakaistanHinnat(data)
                    return
                case "MobiiliverkonKattavuus":
                    apiData = await this.laajakaistaJaPuhelinService.handleDataMobiiliverkonKattavuus(data)
                    legend = this.laajakaistaJaPuhelinService.getMVSaatavuusLegend(data)
                    break
                case "MobiiliverkonKattavuusModal":
                    await this.laajakaistaJaPuhelinService.handleDataMobiiliverkonHinnat(data)
                    return
                case "OikeusPuhelinJaLaajakaistaLiittymaanPalvelutyypinMukaan":
                    apiData = await this.laajakaistaJaPuhelinService.handleDataOikeus(data, false)
                    break
                case "OikeusPuhelinJaLaajakaistaLiittymaanSijainninMukaan":
                    return await this.laajakaistaJaPuhelinService.handleDataOikeus(data, true)
                case "TVPeittoalueet":
                case "RadioPeittoalueet":
                    apiData = await this.tvJaRadioService.handleDataPeittoalueet(data)
                    break
                case "AntenniTVSaatavuus":
                    apiData = await this.tvJaRadioService.handleDataSaatavuus(data)
                    break
                case "TVKanavatModal":
                    await this.tvJaRadioService.handleDataTVKanavat(data)
                    return
                case "RadioasemienKuuluvuus":
                    await this.tvJaRadioService.handleDataKuuluvuus(data)
                    break
                case "Infokartta":
                    apiData = await this.infokartatService.handleDataInfokartta(data)
                    if (apiData !== null && apiData !== undefined && apiData.length > 0) {
                        if (apiData[0].featureData !== null && apiData[0].featureData !== undefined) {
                            // Set Iframe title
                            if (this.activeLang.toString() === "fi") {
                                store.dispatch("setIframeTitle", apiData[0].featureData.nimifi)
                            }
                            else if (this.activeLang.toString() === "sv") {
                                store.dispatch("setIframeTitle", apiData[0].featureData.nimisv)
                            }
                            else {
                                store.dispatch("setIframeTitle", apiData[0].featureData.nimien)
                            }
                        }

                        if (apiData[0].featureData.dim_karttalegendtyyppi_id !== null && apiData[0].featureData.dim_karttalegendtyyppi_id !== undefined) {
                            switch (apiData[0].featureData.dim_karttalegendtyyppi_id) {
                                case 1:
                                    legend = await this.infokartatService.getAlueTyyppiLegend()
                                    break
                                case 2:
                                    legend = await this.infokartatService.getPisteTyyppiLegend()
                                    break
                                case 3:
                                    legend = await this.infokartatService.getReittiTyyppiLegend()
                                    break
                                case 0:
                                default:
                                    legend = ""
                                    break
                            }
                        }
                    }
                    break
                case "KiinteanVerkonSaatavuusRuuduilla":
                    apiData = await this.laajakaistaJaPuhelinService.handleKiinteanVerkonSaatavuusRuudut(data)
                    legend = this.laajakaistaJaPuhelinService.getKVSaatavuusRuudutLegend()
                    break
                case "ViatJaHairiot":
                    apiData = await this.viatJaHairiotService.handleDataViatJaHairiot(data)
                    legend = this.viatJaHairiotService.getViatJaHairiotLegend()
                    break
                case "Radiomikrofonitaajuudet":
                    apiData = await this.radiomikrofonitaajuudetService.handleRadiomikrofonitaajuudet(data)
                    break
                case "BittimittariVuorokaudenaikaanPohjautuvatTiedot":
                    apiData = await this.bittimittariService.handleBittimittariVuorokausi(data)
                    // Legendia ei näytetä koko maa näkymässä
                    if (apiData.length > 0 && (apiData[0].featureData.kuntanumero !== null || apiData[0].featureData.maakuntanumero !== null)) {
                        legend = this.bittimittariService.getBittimittariVuorokausiLegend(data)
                    }
                    break
                case "BittimittariOperaattorikohtaisetTiedot":
                    apiData = await this.bittimittariService.handleBittimittariOperaattorikohtaisetTiedot(data)
                    // Legendia ei näytetä koko maa näkymässä

                    if (apiData.length > 0 && (apiData[0].featureData.kuntanumero !== null || apiData[0].featureData.maakuntanumero !== null)) {
                        // tarkistetaan storesta, onko käyttöliittymässä valittuna download vai upload -valinta ja legend määräytyy sen mukaan
                        const dataDirection = store.getters.dataTransferDirection
                        if (dataDirection === "Download") {
                            legend = this.bittimittariService.getBittimittariOperaattoriDownloadLegend(data)
                        }
                        else if (dataDirection === "Upload") {
                            legend = this.bittimittariService.getBittimittariOperaattoriUploadLegend(data)
                        }
                    }
                    break
                case "BittimittariVuorokaudenaikaanPohjautuvatRuudut":
                    apiData = await this.bittimittariService.handleBittimittariVuorokaudenaikaanPohjautuvatRuudut(data)
                    legend = this.bittimittariService.getBittimittariVuorokaudenaikaanPohjautuvatRuudutLegend()
                    break
                default:
                    // TODO muut api kutsut ApiModel muotoon
                    break
            }

            this.currentLayer = type

            // Tyhjännetään currentApiData, niin ei jää edelliselta backend kutsusta tietoa
            this.currentApiData = []

            // Poistetaan vanha data kartalta, ennen kuin aletaan piirtämään uutta
            this.clearMap()
            // piirretään kartalle data
            if (apiData.length > 0 || legend !== "") {
                // piirretään kartalle data
                await this.drawAreas(apiData, legend, type)
                this.apiData = apiData
                if (type === "Liikenneonnettomuudet"
                    || type === "KiinteanLaajakaistanSaatavuus"
                    || type === "MobiiliverkonKattavuus"
                    || type === "PostiTarkasteleMaaraa"
                    || type === "EnsirekisteroidytHenkiloAutot"
                    || type === "EnsirekisteroidytHenkiloAutotPaastot"
                    || type === "LiikennekaytossaHenkiloAutot"
                    || type === "LiikennekaytossaHenkiloAutotKeskiIka"
                    || type === "MyonnetytTaksiluvat"
                    || type === "VoimassaOlevatAjokortit"
                    || type === "RekisteroidytVesikulkuneuvot"
                    || type === "ViatJaHairiot"
                    || type === "BittimittariVuorokaudenaikaanPohjautuvatTiedot"
                    || type === "BittimittariOperaattorikohtaisetTiedot") {
                    // Valitaan kunta tai maakunta kartalta jos on valittuna dropdownissa
                    this.municipalityOrProvinceChanged(type)
                }
            }
            else {
                // Resetoidaan layer notifikaatio
                this.removeLayerNotification()
                store.dispatch("setLayersVisible", true)
            }

            // Lasketaan kartalla näkyvät eri tyyppisten markkereiden määrät erikseen
            await this.countVisibleMarkers(type)

            store.dispatch("setApiData", apiData)
        }

        // Piirretään alueet kartalle
        async drawAreas(areas: Array<ApiDataModel>, legend: string, layerType: string): Promise<void> {
            if (this.lMap.hasLayer(this.dataLayer)) {
                this.dataLayer.removeFrom(toRaw(this.lMap)) // poistetaan datalayeri kartalta -> nopeuttaa kohteiden luontia ja lisäämistä datalayerille
            }
            if (areas) {
                for (let i = 0; i < areas.length; i++) {
                    const feature = areas[i]

                    if (feature.geometryData) {
                        let layeri = this.geometryToLayer(feature.geometryData, feature.convertGeometry, feature.featureData.tyyppi ?? null, feature.geometryData.allowedGeometryTypes ?? null, feature.geometryData.options?.fillColor)

                        if (layeri) {
                            // Popup täytetään kohteen servicessä
                            if (feature.featureData.popup) {
                                if (layeri instanceof L.FeatureGroup) {
                                    // FeatureGroupissa popup pitää lisätä jokaiselle member layerille erikseen
                                    const featureGroup = layeri as L.FeatureGroup
                                    featureGroup.eachLayer((layer) => {
                                        layer.bindPopup(toRaw(feature.featureData.popup))
                                    })
                                }
                                else {
                                    layeri.bindPopup(toRaw(feature.featureData.popup))
                                }
                            }

                            if (!this.disableLayerEvents) {
                                layeri = layeri.on("click", (e) => {
                                    this.mapObjectClicked(e, feature.id, feature.geometryData?.type ?? "", feature.featureData.popup ?? null, layerType)
                                })

                                if (layerType === "PostiHaeToimipaikkoja" || layerType === "LupaJaRekisterointitoimipaikat" || layerType === "Rekisterointitoimipaikat") {
                                    layeri = layeri.on("keydown", (e: L.LeafletEvent) => {
                                        this.mapObjectClicked(e, feature.id, feature.geometryData?.type ?? "", feature.featureData.popup ?? null, layerType)
                                    })
                                }
                            }
                            if (feature.geometryData.type !== "undefined" && (feature.geometryData.type === GeometryTypes.Point)) {
                                this.markerClusterGroup.addLayer(toRaw(layeri))
                            }
                            else if (feature.geometryData.type !== "undefined" && feature.geometryData.type === GeometryTypes.GeometryCollection && layeri instanceof L.FeatureGroup) {
                                // FeatureGroupissa olevat layerit pitää käsitellä yksitellen
                                const featureGroup = layeri as L.FeatureGroup
                                featureGroup.eachLayer((layer) => {
                                    if (layer instanceof L.Marker) {
                                        this.markerClusterGroup.addLayer(layer)
                                    }
                                    else {
                                        this.dataLayer.addLayer(layer)
                                    }
                                })
                            }
                            else {
                                this.dataLayer.addLayer(layeri)
                            }
                            if (feature.rightSlotData) {
                                this.currentApiData.push(feature.rightSlotData)
                            }
                            feature.layer = layeri
                        }
                    }
                }
                this.dataLayer.addLayer(this.markerClusterGroup)

                if (this.markerClusterGroup) { // jos markerClusterGroup => lisätään sen sisältämille markereille alt-tekstit
                    this.addAltTextToMarkers(this.markerClusterGroup, this.dataUrlToLoad.type)
                }
            }
            if (legend) {
                store.dispatch("setLegend", legend)
            }

            this.checkZoomLevel()
        }

        /** Muutetaan tuodut geometriat ensiksi geometrian tyypin mukaisesti oikeaan muotoon: ETRS89(EUREF-FIN, EPSG:3067) -> WGS84(EPSG:4326)
         * ja sen jälkeen leafletin lat/lon koordinaateiksi ja kartan layereiksi */
        geometryToLayer(geojson, convertCoords: boolean, layeroptions?, allowedTypes?: GeometryTypes[] | null, fillColor?) {
            /** Käydään läpi kaikki löytyvät arrayt ja muunnetaan niiden koordinaatit (case multipolygon jossa voi olla vaihteleva määrä taulukoita eri syvyyksillä ja määrillä) */
            const printArray = function (arr) {
                try {
                    if (arr instanceof Array) {
                        for (let g = 0; g < arr.length; g++) {
                            if (arr[g] instanceof Array) {
                                printArray(arr[g])
                            }
                            else {
                                const temp = proj4("EPSG:3067", "EPSG:4326", arr)
                                arr[g] = temp[0]
                                arr[g + 1] = temp[1]
                                break
                            }
                        }
                    }
                    else arr = proj4("EPSG:3067", "EPSG:4326", arr)
                }
                catch (error) {
                    // TODO virheen käsittely niin ettei yksi viallinen geometria keskeytä koko ajoa
                    console.log("Virhe geometrian muunnoksessa:" + error)
                }
            }
            /** tarkistetaan onko sallitun tyyppinen geometria */
            const checkIsAllowed = function (type) {
                if (allowedTypes) {
                    const indexOf = Object.values(GeometryTypes).indexOf(type as unknown as GeometryTypes)
                    if (indexOf < 0) {
                        return false
                    } // joku tuntematon geometria tyyppi
                    if (allowedTypes.indexOf(Object.values(GeometryTypes)[indexOf]) > -1) return true
                    return false
                }
                return true
            }

            const geometry = geojson
            const options = geojson.options ? geojson.options : layeroptions
            // Oletus markerin ulkoasu. Korvattava servicessä halutun laiseksi
            // TODO: Laajakaistahankkeissa tulee testi datassa yhdessä kohteessa piste koordinaatti mukana
            let markerOptions = {
                id: 0,
                icon: L.icon({
                    iconUrl: this.MarkerUtils.createLocationMarkerByColor(fillColor),
                    iconAnchor: [12, 40],
                    iconSize: [24, 40],
                    popupAnchor: [0, -30]
                }),
                color: fillColor
            }
            markerOptions = geojson.markerOptions ? geojson.markerOptions : markerOptions

            let coords = geometry ? geometry.coordinates : null
            const layers: L.Layer[] = []
            const _coordsToLatLng = L.GeoJSON.coordsToLatLng

            let latlng, latlngs, i, len

            if (!coords && !geometry) {
                return null
            }
            // tarkistetaan onko sallitun tyyppinen geometria
            if (!checkIsAllowed(geometry.type)) return null

            switch (geometry.type) {
                case GeometryTypes.Point:
                    if (convertCoords) {
                        coords = proj4("EPSG:3067", "EPSG:4326", coords)
                        latlng = _coordsToLatLng(coords)
                    }
                    else {
                        latlng = coords
                    }
                    return new L.Marker(latlng, markerOptions)

                case GeometryTypes.MultiPoint:
                    for (i = 0, len = coords.length; i < len; i++) {
                        if (convertCoords) {
                            printArray(coords[i])
                            latlng = _coordsToLatLng(coords[i])
                        }
                        else {
                            latlng = coords
                        }
                        layers.push(new L.Marker(latlng, markerOptions))
                    }
                    return new L.FeatureGroup(layers)

                case GeometryTypes.LineString:
                case GeometryTypes.MultiLineString:
                    if (convertCoords) {
                        printArray(coords)
                        latlngs = L.GeoJSON.coordsToLatLngs(coords, geometry.type === GeometryTypes.LineString ? 0 : 1, _coordsToLatLng)
                    }
                    else {
                        latlngs = coords
                    }
                    return new L.Polyline(latlngs, options)

                case GeometryTypes.Polygon:
                case GeometryTypes.MultiPolygon:
                    // ("polygon/multipolygon")
                    if (convertCoords) {
                        printArray(coords)
                        latlngs = L.GeoJSON.coordsToLatLngs(coords, geometry.type === GeometryTypes.Polygon ? 1 : 2, _coordsToLatLng)
                    }
                    else {
                        latlngs = coords
                    }
                    return new L.Polygon(latlngs, options)

                case GeometryTypes.GeometryCollection:
                    for (i = 0, len = geometry.geometries.length; i < len; i++) {
                        const layer = this.geometryToLayer({
                            coordinates: geometry.geometries[i].coordinates,
                            type: geometry.geometries[i].type
                        }, convertCoords, options, allowedTypes, fillColor)

                        if (layer) {
                            layers.push(layer)
                        }
                    }
                    return new L.FeatureGroup(layers)

                default:
                    throw new Error("Invalid GeoJSON object.")
            }
        }

        addAltTextToMarkers(markerClusterGroup, type) {
            markerClusterGroup.eachLayer((marker) => {
                const markerType = (marker as any).options.type ?? ""
                marker.on("add", () => {
                    let markerTypeTranslated = ""
                    const markerImg = toRaw(marker.getElement())
                    if (type === "PostiHaeToimipaikkoja") {
                        markerTypeTranslated = this.$t(getPostiLangTerm(markerType)) ?? ""
                    }
                    else if (type === "LupaJaRekisterointitoimipaikat" || type === "Rekisterointitoimipaikat") {
                        markerTypeTranslated = this.$t(getLiikenneLangTerm(markerType)) ?? ""
                    }
                    if (markerImg) {
                        markerImg.alt = this.$t("App.Map.MarkerText") + " " + markerTypeTranslated
                    }
                })
            })
        }

        /** Laskee kartalla näkyvien markkereiden määrän
         * ja tallentaa ne tyyppi kohtaisesti visiblemarkers (Map<string , number>) muuttujaan*/
        async countVisibleMarkers(type: string) {
            this.visiblemarkers = null
            this.markersData = null
            switch (type) {
                case "PostiHaeToimipaikkoja":
                    // Eritellään ne posti "tyypit" jotka halutaan laskea ruudulta
                    this.visiblemarkers = new Map<string, number>([
                        ["postitoimipiste", 0],
                        ["noutopiste", 0],
                        ["postilokero", 0],
                        ["pakettiautomaatti", 0],
                        ["kirjelaatikko", 0],
                        ["yrityspiste", 0]
                    ])
                    this.markersData = new Map<string, string>([
                        ["postitoimipiste", ""],
                        ["noutopiste", ""],
                        ["postilokero", ""],
                        ["pakettiautomaatti", ""],
                        ["kirjelaatikko", ""],
                        ["yrityspiste", ""]
                    ])
                    break
                case "LupaJaRekisterointitoimipaikat": // tieliikenne
                    this.visiblemarkers = new Map<string, number>([
                        ["ajokorttijalupapalvelupiste", 0],
                        ["rekisterointitoimipaikka", 0]
                    ])
                    this.markersData = new Map<string, string>([
                        ["ajokorttijalupapalvelupiste", ""],
                        ["rekisterointitoimipaikka", ""]
                    ])
                    break
                case "Rekisterointitoimipaikat": // vesiliikenne
                    this.visiblemarkers = new Map<string, number>([
                        ["yleisetpisteet", 0],
                        ["omatrekisteroinnit", 0]
                    ])
                    this.markersData = new Map<string, string>([
                        ["yleisetpisteet", ""],
                        ["omatrekisteroinnit", ""]
                    ])
                    break
                default:
                    // TODO muut tyypit joissa pitää laskea näkyviä määriä
                    break
            }
            const clusteredMarkers = this.markerClusterGroup

            if (this.visiblemarkers) {
                let parent
                const visibleClusterMarkers: any = []
                const bounds = this.lMap.getBounds()
                let teksti = ""
                this.visiblemarkers?.forEach((value, key, map) => {
                    teksti = ""
                    clusteredMarkers.eachLayer(function (layer) {
                        parent = clusteredMarkers.getVisibleParent(layer)
                        if (parent && (typeof visibleClusterMarkers[parent._leaflet_id] === "undefined")) {
                            visibleClusterMarkers[parent._leaflet_id] = parent
                        }
                    })
                    const allVisibleMarkers: any = []
                    visibleClusterMarkers.forEach((clusterMarker) => {
                        if (bounds.contains(clusterMarker._latlng)) {
                            if (clusterMarker._childCount > 1) {
                                // lisätään aria-label kartan marker klustereille
                                const clusterDivElement = toRaw(clusterMarker.getElement())
                                const ariaLabelText = this.$t("App.Map.ClusterGroupAriaLabel", { count: clusterMarker._childCount })
                                clusterDivElement.setAttribute("aria-label", ariaLabelText)

                                const allChildMarkers = clusterMarker.getAllChildMarkers()
                                for (let i = 0; i < allChildMarkers.length; i++) {
                                    if (allChildMarkers[i] instanceof L.Marker) {
                                        allVisibleMarkers.push(allChildMarkers[i])
                                    }
                                }
                            }
                            else if (clusterMarker instanceof L.Marker) {
                                allVisibleMarkers.push(clusterMarker)
                            }
                        }
                    })
                    allVisibleMarkers.forEach((marker) => {
                        const markerType = (marker as any).options.type ?? ""
                        if (markerType === key || type === "" || type == null) {
                            value += 1
                            map.set(key, value)
                            // postin tilanteessa markerdataan kerätään ID:tä joten erotellaan pilkulla
                            if (type === "PostiHaeToimipaikkoja") {
                                if (value !== 1) teksti += ","
                                teksti += (marker as any).options.id ?? ""
                            }
                            else {
                                // Modaliin menevät teksti pitää vielä tyypittää koska voidaan valita tyypin mukaisesti mitä halutaan esittää
                                if ((marker as any)._popup._content) {
                                    teksti += "<br/>" + (marker as any)._popup._content
                                }
                            }
                        }
                    })
                    this.markersData?.set(key, teksti)
                })
            }
            store.dispatch("setVisibleMarkers", this.visiblemarkers)
            store.dispatch("setMarkersData", this.markersData)
        }

        async mapObjectClicked(e, featureId: string, geometryType: string, originalPopupText: string, layerType: string): Promise<void> {
            await this.clearSelectedMarker()

            // haetaan ID:llä yksittäisen postitoimipisteen tiedot ja muokataan ne popup-ikkunaan
            if (layerType === "PostiHaeToimipaikkoja") {
                let popuptext = ""
                const clickedLayer: L.Layer = toRaw(e.target)
                const data = await this.apiService.getPopupDetailsPosti(featureId)
                popuptext += await this.postiService.createPopuptext(data)

                clickedLayer.setPopupContent(popuptext)
            }

            let selectedData: DynamicRightSlotData | undefined = this.currentApiData.find(a => a.id === featureId)
            // Päivitetään oikean komponentin taulukko. Tallennus storen kautta
            if (layerType === "EnsirekisteroidytHenkiloAutot"
                || layerType === "EnsirekisteroidytHenkiloAutotPaastot"
                || layerType === "LiikennekaytossaHenkiloAutot"
                || layerType === "LiikennekaytossaHenkiloAutotKeskiIka") {
                // Haetaan valitun kunnan/maakunnan tiedot rightslotiin DataPublishServiceltä
                let data = null
                const selection = store.getters.selectedAreaRadioButton
                if (selection === "kunta") {
                    data = await this.apiService.getLiikenteenTilastotKuntaData(layerType, featureId)
                }
                else if (selection === "maakunta") {
                    data = await this.apiService.getLiikenteenTilastotMaakuntaData(layerType, featureId)
                }
                if (data !== null) {
                    if (layerType === "EnsirekisteroidytHenkiloAutot") {
                        selectedData = this.tieliikenneService.handleHenkiloAutotCountsRightslotData(featureId, data, selectedData?.municipality ?? "")
                    }
                    else if (layerType === "EnsirekisteroidytHenkiloAutotPaastot") {
                        selectedData = this.tieliikenneService.handleEnsirekisteroidytHenkiloAutotPaastotRightslotData(featureId, data, selectedData?.dataUpdatedDate ?? "", selectedData?.municipality ?? "")
                    }
                    else if (layerType === "LiikennekaytossaHenkiloAutot") {
                        selectedData = this.tieliikenneService.handleHenkiloAutotCountsRightslotData(featureId, data, selectedData?.municipality ?? "")
                    }
                    else if (layerType === "LiikennekaytossaHenkiloAutotKeskiIka") {
                        selectedData = this.tieliikenneService.handleLiikennekaytossaHenkiloAutotKeskiIkaRightslotData(featureId, data, selectedData?.dataUpdatedDate ?? "", selectedData?.municipality ?? "")
                    }
                }
            }
            else if (layerType === "BittimittariVuorokaudenaikaanPohjautuvatTiedot") {
                // Haetaan valitun kunnan/maakunnan tiedot rightslotiin DataPublishServiceltä
                let data = null
                const selection = store.getters.selectedAreaRadioButton
                if (selection === "kunta") {
                    data = await this.apiService.getBittimittariVuorokausiKuntaData(featureId)
                }
                else if (selection === "maakunta") {
                    data = await this.apiService.getBittimittariVuorokausiMaakuntaData(featureId)
                }
                if (data !== null) {
                    selectedData = this.bittimittariService.handleBittimittariVuorokausiRightslotData(featureId, data, selectedData?.municipality ?? "")
                }
            }
            else if (layerType === "BittimittariOperaattorikohtaisetTiedot") {
                // Haetaan valitun kunnan/maakunnan tiedot rightslotiin DataPublishServiceltä
                let data = null
                const selection = store.getters.selectedAreaRadioButton

                // Haetaan valitun operaattorin id
                const operatorId = store.getters.operatorId

                if (selection === "kunta") {
                    data = await this.apiService.getBittimittariOperaattoriKuntaData(featureId, operatorId ?? -1)
                }
                else if (selection === "maakunta") {
                    data = await this.apiService.getBittimittariOperaattoriMaakuntaData(featureId, operatorId ?? -1)
                }
                if (data !== null) {
                    selectedData = this.bittimittariService.handleOperaattorikohtaisetTiedotRightslotData(featureId, data, selectedData?.municipality ?? "")
                }
            }
            if (selectedData) {
                store.dispatch("setRightSlotData", selectedData)
            }

            // siivotaan vanhat korostukset pois alueilta
            this.clearSelections()

            // tunnistus onko alue vai piste. pisteeltä ei voi lisätä viivankorostuksia samalla tavalla kuin alueilta eikä tarvetta tarkistaa päällekkäisyyksiä (vielä)
            if (geometryType !== GeometryTypes.Point && geometryType !== GeometryTypes.MultiPoint) {
                const layersClicked: L.Layer[] = []

                if ((e as L.LeafletMouseEvent).latlng == null && this.selectedLayer != null) {
                    // Generoitu click event (esim. kunta tai maakunta valittu dropdownista). Ei sisällä koordinaatteja joten otetaan layer muuttujasta.
                    layersClicked.push(this.selectedLayer)
                    // this.selectedLayer = null // miksi nullataan?, jos null, niin seuraava dropdownista faila, kun sielläkään ole koordinaatteja
                }
                else {
                    if (layerType === "Liikenneonnettomuudet"
                        || layerType === "KiinteanLaajakaistanSaatavuus"
                        || layerType === "MobiiliverkonKattavuus"
                        || layerType === "PostiTarkasteleMaaraa"
                        || layerType === "EnsirekisteroidytHenkiloAutot"
                        || layerType === "EnsirekisteroidytHenkiloAutotPaastot"
                        || layerType === "LiikennekaytossaHenkiloAutot"
                        || layerType === "LiikennekaytossaHenkiloAutotKeskiIka"
                        || layerType === "MyonnetytTaksiluvat"
                        || layerType === "VoimassaOlevatAjokortit"
                        || layerType === "RekisteroidytVesikulkuneuvot"
                        || layerType === "ViatJaHairiot"
                        || layerType === "BittimittariVuorokaudenaikaanPohjautuvatTiedot"
                        || layerType === "BittimittariOperaattorikohtaisetTiedot") {
                        // Valitaan kunta tai maakunta kartalta ja siirretään valinta dropdowniin
                        this.municipalityOrProvinceChangedFromMap(featureId)
                    }
                    // TODO VIat Ja häiriöt: Koska polygonit (maakunta ja häiriöt) päällekkäin, miten tiedettään mitä haluttiin klikata oikeasti
                    // + jos häiriöalue on pienempi kuin maakunta niin sen klikkaamisessa on myös ongelmia. Ja kuinka korostetaan maakunta ilman että sekoittaa graafisesti häiriön korostuksen?
                    if (layerType === "ViatJaHairiot" && !featureId.includes("Fault_", 0)) {
                        // klikattu karttaa ja suodatettava näytettävä data maakunnan mukaan.
                        await this.viatJaHairiotService.getViatJaHairiot((store.getters.rightSlotData as DynamicRightSlotData))
                    }
                    // tarkistetaan onko päällekkäisiä tasoja ja kerätään ne muistiin
                    const { lat, lng } = (e as L.LeafletMouseEvent).latlng
                    // klikkauksen koordinaatit
                    const point = turf.point([lng, lat])
                    // klikattu koordinaatit tallennetaan, niin voidaan watch:ta tarvittaessa
                    this.saveCoordinates(e)

                    // loop all data layers save data layers that have been clicked
                    this.dataLayer.eachLayer(layer => {
                        const geoJSON = (layer as any).toGeoJSON()
                        switch (geoJSON.type) {
                            case "Feature":
                                if (geoJSON.geometry.type !== GeometryTypes.Point && geoJSON.geometry.type !== GeometryTypes.MultiPoint) {
                                    if (geoJSON.geometry.type === GeometryTypes.LineString || geoJSON.geometry.type === GeometryTypes.MultiLineString) {
                                        if ((layer as any).options.id !== null && (layer as any).options.id !== undefined
                                            && selectedData?.id === (layer as any).options.id.toString()) {
                                            // Reitti
                                            (layer as any).setStyle({
                                                weight: 6,
                                                color: (layer as any).options.fillColor
                                            })
                                            layersClicked.push(layer)
                                        }
                                    }
                                    else if (booleanPointInPolygon(point, geoJSON)) {
                                        // Polygon
                                        layersClicked.push(layer)
                                    }
                                }
                                break
                            case "FeatureCollection":
                                geoJSON.features.forEach((x) => {
                                    if (x.geometry.type !== GeometryTypes.Point && x.geometry.type !== GeometryTypes.MultiPoint) {
                                        if (x.geometry.type === GeometryTypes.LineString || x.geometry.type === GeometryTypes.MultiLineString) {
                                            if ((layer as any).options.id !== null && (layer as any).options.id !== undefined
                                                && selectedData?.id === (layer as any).options.id.toString()) {
                                                // Reitti
                                                (layer as any).setStyle({
                                                    weight: 6,
                                                    color: (layer as any).options.fillColor
                                                })
                                                layersClicked.push(layer)
                                            }
                                        }
                                        else if (booleanPointInPolygon(point, x)) {
                                            // Polygon
                                            layersClicked.push(layer)
                                        }
                                    }
                                })
                                break
                            default:
                                break
                        }
                    })
                }

                // highlight clicked polygon and set view to it
                layersClicked.forEach(element => {
                    let targetElement: L.Layer | undefined
                    // laajakaistahankkeet ja infokartta polygonien tapauksessa korostetaan kaikkien niiden alueiden reunaviivat joihin klikkaus kohdistuu
                    // muissa tapauksissa korostetaan idn perusteella oikea polygon
                    if (layerType === "Laajakaistahankkeet" || layerType === "Infokartta") {
                        targetElement = element
                    }
                    else {
                        // mikäli käyttäjä painaa tarkalleen kahden polygonin rajalta, valitun alueen data ja klikkauksen kohde-polygonin id:t
                        // saattavat poiketa toisistaan. Vältetään näytettävän datan ja kartalla korostuneen alueen ristiriita ottamalla muistiin
                        // polygon, jossa datavalinta ja kohteen id täsmäävät.
                        if (selectedData?.id === (element as any).options.id?.toString()) {
                            targetElement = element
                        }
                    }

                    // if (layerType === "ViatJaHairiot") {
                    //     // vika/häiriö, mutta alla olevaa maakuntaa alla ei saa korostaa
                    //     const id = featureId.replace("Fault_", "")
                    //     if ((element as any).options.id.toString() === id && featureId.includes("Fault_", 0)) {
                    //         (element as any).setStyle({
                    //             weight: 3,
                    //             color: "#002C74"
                    //         })
                    //     }
                    // }
                    // else {

                    const geoJSON = (element as any).toGeoJSON()
                    if (geoJSON.geometry !== undefined && (geoJSON.geometry.type === GeometryTypes.Polygon || geoJSON.geometry.type === GeometryTypes.MultiPolygon)) {
                        // Muodostetaan valitun polygonin/polygonien pisteiden perusteella uusi erillinen polyline
                        // Asetetaan polylinelle haluttu tyyli ja tuodaan se eteen niin että kuitenkin mikäli
                        // polygonin sisällä on muita alueita, ne pysyvät edelleen klikattavina myös
                        const outlineCoords = (element as any)._latlngs

                        outlineCoords.forEach((coords) => {
                            // Lisätään ensimmäinen piste vielä uudelleen, jotta myös ensimmäinen ja viimeinen piste yhdistyvät
                            const closedPolylineCoords = [...coords, coords[0]]

                            const polyline: L.Polyline = L.polyline(closedPolylineCoords, {
                                weight: 3,
                                color: "#002C74",
                                opacity: 0.7
                            })
                            this.dataLayer.addLayer(polyline)
                        })
                    }
                    if (targetElement !== undefined) {
                        this.setSelectedAreaToView(targetElement)
                    }
                })

                const clickedLayer: L.Layer = toRaw(e.target)
                // kootaan popuppiin kaikkien päällekkäisten layereiden tiedot
                clickedLayer.setPopupContent(originalPopupText)

                if (layersClicked.length > 1) {
                    let popuptext = ""
                    layersClicked.forEach((element) => {
                        // lisätään kaikkien päällekäisten layereiden popuppien tekstit paitsi klikatun -> estetään popupin sisällön toisto kun layeriä klikataan monta kertaa
                        // TODO viatJahäriöt tuplaantuu kun id alkaa Fault_ tekstillä eikä ole pelkkä numero
                        if ((element as any).options.id !== featureId) {
                            if (clickedLayer instanceof L.FeatureGroup) {
                                // Varsinainen klikattu layer kuuluu FeatureGroupiin. Ei lisätä sen popup tekstiä jottei sisältö toistu kun layeriä klikataan monta kertaa.
                                const layerId = (element as any)._leaflet_id
                                const actualLayer = (clickedLayer as any)._layers[layerId]
                                if (actualLayer === undefined) {
                                    // Käsitelty layer ei ole varsinainen klikattu layer, joten lisätään sen popup teksti
                                    popuptext += (element as any)._popup?._content ?? ""
                                }
                            }
                            else {
                                popuptext += (element as any)._popup?._content ?? ""
                            }
                        }
                    })
                    if (popuptext !== "") {
                        if (clickedLayer instanceof L.FeatureGroup) {
                            // Varsinainen klikattu layer kuuluu FeatureGroupiin. Popup teksti pitää asettaa varsinaiselle layerille, ei FeatureGroupille.
                            // Käytetään originalPopupTextia ja lisätään siihen muiden päällekkäisten layerien popup tekstit.
                            // Näin estetään popupin sisällön toisto kun layeriä klikataan monta kertaa.
                            const actualLayerId = e.layer._leaflet_id
                            const actualLayer = e.target._layers[actualLayerId]
                            actualLayer.setPopupContent(originalPopupText + popuptext)
                        }
                        else {
                            clickedLayer.setPopupContent(popuptext)
                        }
                    }

                    if (layerType === "Infokartta") {
                        // lisää right slottiin klikattujen polygonien kuvaukset
                        const rightSlotData = new DynamicRightSlotData(layersClicked[0].toString())

                        layersClicked.forEach((element: any) => {
                            const clickedInfomapData = this.currentApiData.find(x => x.id === element.options.id.toString())

                            rightSlotData?.infomapDetails?.push({ textContent: clickedInfomapData?.textContent, link: clickedInfomapData?.linkki })
                        })
                        store.dispatch("setRightSlotData", rightSlotData)
                    }
                }
            }

            if (layerType === "Infokartta") {
                // Jos kyseessä on infokartalla oleva marker, näytetään valitulle pisteelle korostettu ikoni
                if (geometryType === GeometryTypes.Point) {
                    this.addSelectedMarker(e.target.options.color, e.latlng)
                }
                else if (e.target._layers !== undefined && e.layer !== undefined) {
                    // Kokoelmassa valittu layer pitää selvittää id:n perusteella
                    const selectedLayer = e.target._layers[e.layer._leaflet_id]
                    if (selectedLayer !== undefined) {
                        const geoJson = (selectedLayer as any).toGeoJSON()
                        if (geoJson.geometry.type === GeometryTypes.Point && selectedLayer !== undefined && selectedLayer._latlng !== undefined) {
                            this.addSelectedMarker(selectedLayer.options.color, [selectedLayer._latlng.lat, selectedLayer._latlng.lng])
                        }
                    }
                }
            }

            const closePopupText = this.arialLabelForClosePopup
            setTimeout(function () {
                const closeButton = toRaw(document.querySelector(".leaflet-popup-close-button") as HTMLButtonElement)
                if (closeButton) {
                    closeButton.focus()
                    closeButton.setAttribute("aria-label", closePopupText)
                }
            }, 0)
        }

        setSelectedAreaToView(element) {
            // move view so that the selected area is completely visible in the view, if it is not already
            // 0.5 added to south and east bounds check to make some edge cases work
            if (element.getBounds().getNorth() > this.lMap.getBounds().getNorth()
                || element.getBounds().getSouth() < this.lMap.getBounds().getSouth() + 0.5
                || element.getBounds().getEast() > this.lMap.getBounds().getEast() - 0.5
                || element.getBounds().getWest() < this.lMap.getBounds().getWest()) {
                this.lMap.fitBounds(element.getBounds().pad(0.5))
            }
        }

        clearMap() {
            // Siivotaan kartta
            this.markerClusterGroup.clearLayers()
            this.dataLayer.clearLayers()
            store.dispatch("setLegend", "")
            // Tyhjännetään currentApiData, niin ei jää edelliselta backend kutsusta tietoa
            this.currentApiData = []
        }

        clearSelections(): void {
            this.dataLayer.eachLayer(l => {
                const geoJSON = (l as any).toGeoJSON()
                if (geoJSON.geometry !== undefined && (geoJSON.geometry.type === GeometryTypes.LineString || geoJSON.geometry.type === GeometryTypes.MultiLineString)) {
                    // Reitti
                    (l as any).setStyle({
                        weight: 4,
                        color: (l as any).options.fillColor
                    })
                }
                else {
                    (l as any).setStyle({
                        weight: 1,
                        color: "#6D6D6D"
                    })
                }
            })
        }

        coordinateSystem(): string {
            return "ETRS-TM35FIN"
        }

        async setMapBounds(): Promise<void> {
            this.geomanMap.setMaxBounds(L.latLngBounds([55, 10], [70, 58]))
            L.control.scale({ imperial: false }).addTo(toRaw(this.geomanMap))
            this.geomanMap.zoomControl.setPosition('topright')
        }

        zoomUpdated(zoom: number) {
            if (this.zoom !== zoom) {
                this.zoom = zoom
                // TODO jostain syystä lataus pyörylä ei mene päälle vaan tästä jatketaan tuonne checkZoomLevelille ja jos on paljon tavaraa kartalla niin koko selain on jumissa ilman pyörylää
                store.dispatch("setLoading", { name: "zoomUpdated", value: true })
                this.checkZoomLevel()
                store.dispatch("setLoading", { name: "zoomUpdated", value: false })
            }
        }

        getMinZoomLevel(): number {
            // minimi-zoomaus, jolla kartalla näytetään dataa
            if (this.dataUrlToLoad.url?.includes("RuutuKlusteritVuorokaudenAika")) {
                return 1.5
            }
            return 5.4
        }

        checkZoomLevel() {
            if (this.dataLayer) {
                const minZoomLevel = this.getMinZoomLevel()
                if (this.zoom < minZoomLevel && (this.currentLayer === "PostiHaeToimipaikkoja" || this.currentLayer === "KiinteanVerkonSaatavuusRuuduilla")) {
                    // jos ollaan liian kaukana niin ei piirretä kohteita ja näytetään ruudulla ilmoitus käyttäjälle
                    const message = this.$t("App.Map.ZoomToView")
                    this.$notify({
                        group: "layerNotification",
                        title: "",
                        text: sanitizeHtml(message),
                        duration: -1,
                        type: "blue-notification"
                    })

                    // console.time("zoomlevelRemove")
                    // poistetaan kohteet kartalta.
                    if (this.lMap.hasLayer(this.dataLayer)) {
                        this.dataLayer.removeFrom(toRaw(this.lMap))
                    }
                    store.dispatch("setLayersVisible", false)
                    // console.timeEnd("zoomlevelRemove")
                }
                else if (this.zoom < 3 && this.currentLayer === "BittimittariVuorokaudenaikaanPohjautuvatRuudut") {
                    const message = this.$t("App.Bittimittari.ZoomToView")
                    this.$notify({
                        group: "layerNotification",
                        title: "",
                        text: sanitizeHtml(message),
                        duration: -1,
                        type: "blue-notification"
                    })
                }
                else {
                    // poistetaan ilmoitus näkyvistä
                    this.removeLayerNotification()
                    // lisätään kohteet kartalle jos niitä ei siellä jo ole.
                    if (this.lMap && !this.lMap.hasLayer(this.dataLayer)) {
                        // console.time("zoomlevelAdd")
                        this.dataLayer.addTo(toRaw(this.lMap))
                        store.dispatch("setLayersVisible", true)
                        // console.timeEnd("zoomlevelAdd")
                    }
                }
            }
        }

        // Poistetaan notifikaatio kartan yläreunasta
        removeLayerNotification() {
            this.$notify({
                group: "layerNotification",
                clean: true
            })
        }

        // eslint-disable-next-line
        async centerUpdated(center): Promise<void> {
            this.center = center
            await this.countVisibleMarkers(this.dataUrlToLoad.type)
        }

        // eslint-disable-next-line
        async boundsUpdated(bounds): Promise<void> {
            this.bounds = bounds
            await this.countVisibleMarkers(this.dataUrlToLoad.type)
        }

        isPointInsideOfMapBounds(userLat: number | undefined, userLng: number | undefined): boolean | undefined {
            if (userLat !== undefined && userLng !== undefined) {
                const point = new L.LatLng(userLat, userLng, 0)
                return this.lMap.getBounds().contains(point)
            }
            else {
                // Syötetyt koordinaatit ovat epävalidit
                return undefined
            }
        }

        zoomToCoordinates(userLat: number | undefined, userLng: number | undefined, includeNearestPoints: boolean, overriddenZoomLevel: number | undefined = undefined): void {
            if (userLat !== undefined && userLng !== undefined) {
                if (includeNearestPoints) {
                    const minZoom = store.getters.locationSearchMinZoom
                    const l = new L.LatLng(userLat, userLng, 0)
                    // Kerätään tähän lähin toimipiste jokaisesta toimipistetyypistä.
                    const nearestPointMap = new Map()
                    this.apiData?.forEach(i => {
                        let distance: number = Number.MAX_VALUE
                        // Tyypit tulee tieliikenteessä eri tavalla kuin vesiliikenteessä. Tällä saadaan uniikki.
                        const tyyppi = i.featureData.tyyppi + (i.featureData.alityyppi ? i.featureData.alityyppi : "")
                        if (nearestPointMap.has(tyyppi)) {
                            distance = nearestPointMap.get(tyyppi).distance
                        }
                        if (l.distanceTo(i.layer.getLatLng()) < distance) {
                            nearestPointMap.set(tyyppi, { distance: l.distanceTo(i.layer.getLatLng()), point: i.layer })
                        }
                    })
                    this.addLocationMarker([userLat, userLng])
                    if (nearestPointMap.size > 0) {
                        // Rakennetaan feature group jossa on oma paikka ja lähin piste jokaisesta toimipistetyypistä.
                        /* eslint-disable @typescript-eslint/no-unused-vars */
                        const group = L.featureGroup([this.locationMarkerLayer, ...(Array.from(nearestPointMap).map(([key, value]) => value.point))])
                        /* eslint-enable */
                        const bounds = group.getBounds().pad(0.4)
                        if (minZoom > this.lMap.getBoundsZoom(bounds)) {
                            // Meni yli zoom rajan, näytetään vain oma piste.
                            this.lMap.flyTo([userLat, userLng], minZoom, { duration: 1 })
                        }
                        else {
                            // Näytetään oma piste ja lähimmät toimipisteet
                            this.lMap.flyTo(bounds.getCenter(), this.lMap.getBoundsZoom(bounds), { duration: 1 })
                        }
                    }
                    else {
                        // Ei löytynyt lähintä, näytetään oma piste. Näin ei pitäisi tapahtua
                        this.lMap.flyTo([userLat, userLng], 10, { duration: 1 })
                    }
                }
                else {
                    // Tarkistetaan yliajetaanko default zoom-taso
                    const zoomLevel = overriddenZoomLevel !== undefined ? overriddenZoomLevel : 10

                    // Ei tehdä lähimmän toimipisteen hakua, näytetään vain oma piste.
                    this.lMap.flyTo([userLat, userLng], zoomLevel, { duration: 1 })
                    this.addLocationMarker([userLat, userLng])
                }
            }
        }

        addLocationMarker(latlng: L.LatLngExpression) {
            this.clearLocationMarker()

            const markerOptions: L.MarkerOptions = {
                icon: L.icon({
                    iconUrl: this.MarkerUtils.createLocationMarker(),
                    iconAnchor: [12, 20],
                    iconSize: [24, 40],
                    popupAnchor: [0, -30]
                })
            }

            // lisätään kartalle uusi markkeri ja samalla ko. markkeri muistiin jotta se voidaan poistaa tarvittaessa hallitusti
            this.locationMarkerLayer = new L.Marker(latlng, markerOptions)
            this.locationMarkerLayer.addTo(toRaw(this.lMap))
        }

        clearLocationMarker() {
            // Poistetaan vanha markkeri
            if (this.lMap !== null && this.lMap.hasLayer(this.locationMarkerLayer)) {
                this.lMap.removeLayer(this.locationMarkerLayer)
            }
        }

        checkLocationMarkerVisibility() {
            // Tarkistetaan, löytyykö kyseisestä aihevalinnasta sijainnin haun elementtiä
            const searchLocationElement = document.getElementsByClassName("search-components")[0] as HTMLElement
            // jos ei löydy, asetetaan sijaintimarkerille opacity 0
            if (searchLocationElement === undefined || searchLocationElement.style.display === "none") {
                (this.locationMarkerLayer as L.Marker).setOpacity(0)
            }
            else { // jos taas löytyy, asetetaan opacityksi 1
                (this.locationMarkerLayer as L.Marker).setOpacity(1)
            }
        }

        setLocationElementVisibility() {
            // Tarkistetaan, löytyykö kyseisestä aihevalinnasta sijainnin haun elementtiä
            const searchLocationElement = document.getElementsByClassName("search-components")[0] as HTMLElement
            if (searchLocationElement === undefined || searchLocationElement.style.display === "none") {
                store.dispatch("setLocationElementVisible", false)
            }
            else {
                store.dispatch("setLocationElementVisible", true)
            }
        }

        // Näytetään korostettu marker ikoni valitun markerin päällä
        async addSelectedMarker(color, latlng: L.LatLngExpression): Promise<void> {
            await this.clearSelectedMarker()

            const markerOptions = {
                id: 1,
                icon: L.icon({
                    iconUrl: this.MarkerUtils.createSelectedLocationMarkerByColor(color),
                    iconAnchor: [14, 44],
                    iconSize: [28, 48],
                    popupAnchor: [0, -30],
                    className: "selectedMarker"
                })
            }
            // lisätään kartalle korostettu markkeri ja samalla ko. markkeri muistiin jotta se voidaan poistaa tarvittaessa hallitusti
            this.selectedMarkerLayer = new L.Marker(latlng, markerOptions)
            this.selectedMarkerLayer.addTo(toRaw(this.lMap))
        }

        async clearSelectedMarker(): Promise<void> {
            // Poistetaan vanha markkeri
            if (this.lMap !== null && this.lMap.hasLayer(this.selectedMarkerLayer)) {
                await this.lMap.removeLayer(toRaw(this.selectedMarkerLayer))
            }
        }

        get selectedMunicipality(): string {
            return store.getters.selectedMunicipality
        }

        get selectedProvince(): string {
            return store.getters.selectedProvince
        }

        get selectedAreaRadioButton(): string {
            return store.getters.selectedAreaRadioButton
        }

        @Watch("selectedAreaRadioButton")
        @Watch("selectedMunicipality")
        @Watch("selectedProvince")
        municipalityOrProvinceChanged(type: string = ""): void {
            const selectedAreaRadioButton = this.selectedAreaRadioButton
            if (selectedAreaRadioButton === "kunta" || selectedAreaRadioButton === "maakunta") {
                // Valitaan kartalta kunta tai maakunta
                let selectedData: ApiDataModel | undefined
                if (selectedAreaRadioButton === "maakunta") {
                    selectedData = this.apiData?.find(a => a.featureData.maakuntanumero === this.selectedProvince && a.featureData.kuntanumero === null)
                }
                else if (selectedAreaRadioButton === "kunta") {
                    selectedData = this.apiData?.find(a => a.featureData.kuntanumero === this.selectedMunicipality)
                }

                if (selectedData !== undefined) {
                    store.dispatch("setRightSlotData", selectedData)

                    // Generoidaan click event valitun kunnan/maakunnan layerille ja jätetään käsittely Mapille
                    if (this.selectedLayer !== selectedData.layer) {
                        this.selectedLayer = selectedData.layer
                        selectedData.layer.fireEvent("click")
                    }
                }
                else {
                    // Tyhjennetään rightslot paitsi ViatJaHäiriöt ja Bittimittari sivuilla ei näin saa tehdä
                    if (type !== "ViatJaHairiot"
                        && type !== "BittimittariVuorokaudenaikaanPohjautuvatTiedot"
                        && type !== "BittimittariOperaattorikohtaisetTiedot"
                        && !this.dataUrlToLoad.url?.includes("VuorokaudenAikaKlusteritKuntaKohtainen")
                        && !this.dataUrlToLoad.url?.includes("VuorokaudenAikaKlusteritMaakuntaKohtainen")
                        && !this.dataUrlToLoad.url?.includes("VerkkoOperaattoriKlusteritKuntaKohtainen")
                        && !this.dataUrlToLoad.url?.includes("VerkkoOperaattoriKlusteritMaakuntaKohtainen")
                    ) {
                        store.dispatch("setRightSlotData", [])
                    }
                    // Tyhjennetään valinta
                    this.clearSelections()
                }
            }
        }

        municipalityOrProvinceChangedFromMap(item: string) {
            // Karttaa on klikattu ja nyt asetetaan kunta/maakunta storeen
            if (!item.includes("Fault_", 0)) { // paitsi jos id alkaa Fault_ tekstillä koska se on viat ja häiriät polygon. Kartalla myös maakuntia viat ja hairiöit sivulla..
                if (this.selectedAreaRadioButton === "kunta") {
                    store.dispatch("setSelectedMunicipality", item)
                }
                else if (this.selectedAreaRadioButton === "maakunta") {
                    store.dispatch("setSelectedProvince", item)
                }
            }
        }

        get arialLabelForClosePopup(): string {
            return this.$t("App.Image.ClosePopupLabel") ?? ""
        }

        arialLabelForMap(): string | undefined {
            return this.$t("App.Image.MapDescription") ?? ""
        }
    }

</script>

<style scoped>
    #map {
        height: 100%;
        width: 100%;
        padding: 0;
    }

    a[title]:hover:after {
        background-color: var(--Traficom-main-color) !important;
    }
</style>
<style>
    /* Poistetaan taustakuva zoom painikkeista */
    #map a.leaflet-control-zoom-out, #map a.leaflet-control-zoom-in {
        background-image: none !important;
    }

    #map .leaflet-popup-content-wrapper {
        border-radius: 0px !important;
    }

    .popuplink {
        color: var(--Traficom-main-color) !important;
    }

        .popuplink:focus {
            outline: 3px solid var(--Traficom-main-color);
            outline-offset: 2px;
        }

    div > .leaflet-popup-content {
        max-height: 270px;
        overflow-x: hidden;
        overflow-y: auto;
    }

    .leaflet-container .leaflet-popup a.leaflet-popup-close-button {
        color: black;
    }

    div.leaflet-container a.leaflet-popup-close-button:focus {
        outline: none;
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.75), 0 0 0 3px #002C74;
    }

    div > .leaflet-popup-content p {
        margin: 6px;
    }

    .blue-notification {
        font-size: 0.875rem !important;
        font-weight: 600;
        text-align: center !important;
        border-left: 5px solid #002C74 !important;
        background: #002C74 !important;
        display: flex;
        padding: 13px;
        margin-bottom: 5px;
        top: 5px !important; /* headerin korkeus + 10px*/
        /* width: 400px !important; */
        left: calc(50% - 200px) !important;
    }

    .leaflet-container .leaflet-popup {
        font-size: 0.875rem;
        font-family: Open Sans;
    }

        .leaflet-container .leaflet-popup a {
            color: var(--Traficom-main-color);
        }

    .popup-refresh-button.hidden {
        display: none;
    }

    .popup-refresh-button {
        display: block;
    }

    .popup-more-button {
        display: block;
    }

    .selectedMarker {
        z-index: 10000 !important;
    }
</style>
